// Ported from heric (heric-front/src/components/ui/DocumentPreviewModal.tsx).
// Full-featured image/PDF preview modal with zoom, rotation, pan, pinch-zoom,
// keyboard shortcuts and download. Used by the messagerie to open/enlarge and
// download images transmitted in a conversation.
import { useState, useRef, useEffect } from "react";
import { Icon } from "./messagerieUi";

const IMAGE_EXTENSIONS = /\.(jpg|jpeg|png|webp|gif|heic|bmp|svg)$/i;

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

export function DocumentPreviewModal({ doc, url, onClose, onDownload, onPrev, onNext, counter }) {
  const nameOrPath = doc.gcs_path || doc.name;
  // Images often come from a content-type, not a filename extension, so also
  // treat anything without a recognised non-image extension as an image when
  // the source is a data URL.
  const isImage =
    IMAGE_EXTENSIONS.test(nameOrPath) ||
    (!/\.pdf$/i.test(nameOrPath) && (url || "").startsWith("data:image"));
  const isPdf = /\.pdf$/i.test(nameOrPath);
  const isMobile = useIsMobile();

  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const pinchStart = useRef(null);
  const containerRef = useRef(null);

  const zoomIn = () => setZoom((z) => Math.min(z * 1.3, 8));
  const zoomOut = () => setZoom((z) => Math.max(z / 1.3, 0.1));
  const resetView = () => { setZoom(1); setRotation(0); setPan({ x: 0, y: 0 }); };
  const rotateCW = () => setRotation((r) => (r + 90) % 360);
  const rotateCCW = () => setRotation((r) => (r - 90 + 360) % 360);

  // Reset the zoom/rotation/pan when switching to another image in a gallery.
  useEffect(() => { setZoom(1); setRotation(0); setPan({ x: 0, y: 0 }); }, [url]);

  // Wheel-to-zoom must use a NON-passive listener so preventDefault() actually
  // stops the chat behind the modal from scrolling. React's onWheel prop is
  // passive by default and silently ignores preventDefault.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !isImage) return;
    const onWheel = (e) => {
      e.preventDefault();
      if (e.deltaY < 0) zoomIn();
      else zoomOut();
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [isImage]);
  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    setDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
  };
  const handleMouseMove = (e) => {
    if (!dragging) return;
    setPan({
      x: dragStart.current.panX + (e.clientX - dragStart.current.x),
      y: dragStart.current.panY + (e.clientY - dragStart.current.y),
    });
  };
  const handleMouseUp = () => setDragging(false);

  // Touch handlers
  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      const t = e.touches[0];
      setDragging(true);
      dragStart.current = { x: t.clientX, y: t.clientY, panX: pan.x, panY: pan.y };
      pinchStart.current = null;
    } else if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinchStart.current = { dist: Math.hypot(dx, dy), zoom };
      setDragging(false);
    }
  };
  const handleTouchMove = (e) => {
    e.preventDefault();
    if (e.touches.length === 1 && dragging) {
      const t = e.touches[0];
      setPan({
        x: dragStart.current.panX + (t.clientX - dragStart.current.x),
        y: dragStart.current.panY + (t.clientY - dragStart.current.y),
      });
    } else if (e.touches.length === 2 && pinchStart.current) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const newZoom = Math.min(8, Math.max(0.1, pinchStart.current.zoom * (dist / pinchStart.current.dist)));
      setZoom(newZoom);
    }
  };
  const handleTouchEnd = () => {
    setDragging(false);
    pinchStart.current = null;
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") { onClose(); return; }
      if (e.key === "ArrowLeft" && onPrev) { onPrev(); return; }
      if (e.key === "ArrowRight" && onNext) { onNext(); return; }
      if (!isImage) return;
      if (e.key === "+" || e.key === "=") zoomIn();
      else if (e.key === "-") zoomOut();
      else if (e.key === "0") resetView();
      else if (e.key === "r") rotateCW();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isImage, onClose, onPrev, onNext]);

  const toolbarBtn = "flex items-center justify-center rounded-lg hover:bg-neutral-200 transition-colors";

  // Mobile: full-screen sheet sliding up from bottom
  const modalStyle = isMobile
    ? { width: "100vw", height: "100dvh", borderRadius: 0 }
    : { width: "80vw", maxWidth: 900, height: "85vh", borderRadius: 16 };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center"
      style={{ background: "rgba(0,0,0,0.7)" }}
      onClick={onClose}
    >
      <div
        className="bg-white shadow-2xl flex flex-col overflow-hidden"
        style={modalStyle}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex flex-row items-center justify-between border-b border-neutral-200 flex-shrink-0"
          style={{ padding: isMobile ? "12px 16px" : "12px 20px" }}
        >
          <div className="flex flex-col min-w-0 flex-1 mr-3">
            <span
              className="font-semibold truncate"
              style={{ fontFamily: "Poppins", fontSize: isMobile ? 13 : undefined }}
            >
              {doc.name}
            </span>
            {doc.created_at && !isMobile && (
              <span className="text-h4 text-neutral-400" style={{ fontFamily: "Poppins" }}>{doc.created_at}</span>
            )}
          </div>
          <div className="flex flex-row items-center gap-1.5 flex-shrink-0">
            {isMobile ? (
              <button
                onClick={onDownload}
                className={toolbarBtn}
                style={{ width: 36, height: 36 }}
                title="Télécharger"
              >
                <Icon name="download" size={16} color="#525252" />
              </button>
            ) : (
              <button
                onClick={onDownload}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-neutral-200 hover:bg-neutral-50 text-h3 font-medium text-neutral-600"
                style={{ fontFamily: "Poppins" }}
              >
                <Icon name="download" size={14} color="#525252" />
                Télécharger
              </button>
            )}
            <button onClick={onClose} className={toolbarBtn} style={{ width: isMobile ? 36 : 32, height: isMobile ? 36 : 32 }}>
              <Icon name="close" size={16} color="#525252" />
            </button>
          </div>
        </div>

        {/* Image toolbar */}
        {isImage && (
          <div
            className="flex flex-row items-center justify-center border-b border-neutral-200 bg-neutral-50 flex-shrink-0"
            style={{ gap: isMobile ? 2 : 4, padding: isMobile ? "6px 8px" : "8px 16px" }}
          >
            <button onClick={zoomOut} className={toolbarBtn} style={{ width: isMobile ? 36 : 32, height: isMobile ? 36 : 32 }} title="Dézoomer (-)">
              <svg width={16} height={16} viewBox="0 0 16 16" fill="none"><path d="M4 8h8" stroke="#525252" strokeWidth={1.5} strokeLinecap="round" /></svg>
            </button>
            <span className="text-h4 text-neutral-500 w-12 text-center select-none" style={{ fontFamily: "Poppins" }}>{Math.round(zoom * 100)}%</span>
            <button onClick={zoomIn} className={toolbarBtn} style={{ width: isMobile ? 36 : 32, height: isMobile ? 36 : 32 }} title="Zoomer (+)">
              <svg width={16} height={16} viewBox="0 0 16 16" fill="none"><path d="M8 4v8M4 8h8" stroke="#525252" strokeWidth={1.5} strokeLinecap="round" /></svg>
            </button>
            <div className="w-px h-5 bg-neutral-300 mx-1" />
            <button onClick={rotateCCW} className={toolbarBtn} style={{ width: isMobile ? 36 : 32, height: isMobile ? 36 : 32 }} title="Rotation gauche">
              <svg width={16} height={16} viewBox="0 0 16 16" fill="none"><path d="M2 6.5A5.5 5.5 0 0 1 12.95 5M2 2.5V6.5H6" stroke="#525252" strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" /><circle cx="8" cy="9" r="4.5" stroke="#525252" strokeWidth={1.2} fill="none" /></svg>
            </button>
            <button onClick={rotateCW} className={toolbarBtn} style={{ width: isMobile ? 36 : 32, height: isMobile ? 36 : 32 }} title="Rotation droite (R)">
              <svg width={16} height={16} viewBox="0 0 16 16" fill="none"><path d="M14 6.5A5.5 5.5 0 0 0 3.05 5M14 2.5V6.5H10" stroke="#525252" strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" /><circle cx="8" cy="9" r="4.5" stroke="#525252" strokeWidth={1.2} fill="none" /></svg>
            </button>
            <div className="w-px h-5 bg-neutral-300 mx-1" />
            <button onClick={resetView} className={toolbarBtn + " px-2 gap-1"} style={{ height: isMobile ? 36 : 32 }} title="Réinitialiser (0)">
              <svg width={14} height={14} viewBox="0 0 14 14" fill="none"><path d="M1 7a6 6 0 1 1 1.46 3.93" stroke="#525252" strokeWidth={1.2} strokeLinecap="round" /><path d="M1 11V7.5H4.5" stroke="#525252" strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" /></svg>
              <span className="text-h4 text-neutral-500" style={{ fontFamily: "Poppins" }}>1:1</span>
            </button>
          </div>
        )}

        {/* Content */}
        <div
          ref={containerRef}
          className="relative flex-1 overflow-hidden flex items-center justify-center bg-neutral-50"
          style={{ cursor: isImage ? (dragging ? "grabbing" : "grab") : "default", touchAction: "none" }}
          onMouseDown={isImage ? handleMouseDown : undefined}
          onMouseMove={isImage ? handleMouseMove : undefined}
          onMouseUp={isImage ? handleMouseUp : undefined}
          onMouseLeave={isImage ? handleMouseUp : undefined}
          onTouchStart={isImage ? handleTouchStart : undefined}
          onTouchMove={isImage ? handleTouchMove : undefined}
          onTouchEnd={isImage ? handleTouchEnd : undefined}
        >
          {(onPrev || onNext) && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); onPrev && onPrev(); }}
                className="absolute z-10 flex items-center justify-center rounded-full hover:bg-black/60"
                style={{ left: 12, top: "50%", transform: "translateY(-50%) rotate(90deg)", width: 40, height: 40, background: "rgba(0,0,0,0.45)", border: "none", cursor: "pointer" }}
                title="Image précédente (←)"
              >
                <Icon name="chevron" size={18} color="#FFFFFF" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onNext && onNext(); }}
                className="absolute z-10 flex items-center justify-center rounded-full hover:bg-black/60"
                style={{ right: 12, top: "50%", transform: "translateY(-50%) rotate(-90deg)", width: 40, height: 40, background: "rgba(0,0,0,0.45)", border: "none", cursor: "pointer" }}
                title="Image suivante (→)"
              >
                <Icon name="chevron" size={18} color="#FFFFFF" />
              </button>
              {counter && (
                <span
                  className="absolute z-10 select-none"
                  style={{ bottom: 12, left: "50%", transform: "translateX(-50%)", background: "rgba(0,0,0,0.55)", color: "#FFFFFF", borderRadius: 999, padding: "3px 12px", fontFamily: "Poppins", fontSize: 12 }}
                >
                  {counter}
                </span>
              )}
            </>
          )}
          {isPdf ? (
            <iframe src={url} className="w-full h-full" style={{ border: "none" }} title={doc.name} />
          ) : isImage ? (
            <img
              src={url}
              alt={doc.name}
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotate(${rotation}deg)`,
                transformOrigin: "center center",
                transition: dragging ? "none" : "transform 0.15s ease",
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                userSelect: "none",
                pointerEvents: "none",
              }}
              draggable={false}
            />
          ) : (
            <div className="flex flex-col items-center gap-3 text-neutral-400">
              <Icon name="file" size={48} color="#D4D4D4" />
              <span className="text-h2 font-medium" style={{ fontFamily: "Poppins" }}>Aperçu non disponible</span>
              <button
                onClick={onDownload}
                className="px-4 py-2 rounded-lg bg-indigo-500 text-white text-h3 font-medium hover:bg-indigo-600"
                style={{ fontFamily: "Poppins" }}
              >
                Télécharger le fichier
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DocumentPreviewModal;
